import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FaPlus, FaSpinner } from "react-icons/fa";
import { DeleteButton } from "../../../../components/Admin";
import { AiOutlineEdit } from "react-icons/ai";
import { Tooltip } from "react-tooltip";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import { VariableSizeList } from "react-window";

import { ModalConfirmWindow } from "../ManageUserTab/components/ModalConfirmWindow/ModalConfirmWindow";
import { ITeam, IOption, ITeamList } from "../../interfaces/team.interface";
import { useUserContext } from "../../../../context/UserContext";
import { fetchNotification } from "./services/fetchNotification.service";
import { SearchTableInput } from "../SearchTableInput/SearchTableInput";
import { canUserEdit } from "../../helpers/userRights";
import { AddEditNotificationModal } from "./components/AddEditNotificationModal";
import { createNotitfaction } from "./services/createNotification.service";
import { updateNotification } from "./services/updateNotification.service";
import { deleteNotification } from "./services/deleteNotification.service";
import { INewNotification, INotification } from "./interfaces/notification.interface";
import { OverflowCell } from "./components/OverflowCell/OverflowCell";

interface IManageUserTabProps {
  darkMode: boolean;
  teams: { value: string; label: string }[];
  isSelectedTeam: boolean;
  selectedTeam: ITeam | null;
  teamId: any;
  teamList: ITeamList[];
  allTeamList: ITeamList[];
  teamOptions: IOption[] | [];
  activeTab: string;
}

type RowKey = string | number;
type ResponsiveMode = "desktop" | "compact";

const HEADER_H = 44;
const FALLBACK_ROW_H = 72;
const LOADER_ROW_H = 56;
const COMPACT_BREAKPOINT = 760;
const VIRTUALIZE_ROW_THRESHOLD = 20;

type UpdateNotificationPayload = (Partial<INewNotification> & { id: number }) & {
  is_active?: boolean;
  regulators?: Array<number | string>;
  classes?: Array<number | string>;
};

const cn = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(" ");

const addPx = (value: unknown, delta: number) => {
  if (typeof value === "number") return value + delta;
  if (typeof value === "string" && value.endsWith("px")) {
    return `${parseFloat(value) + delta}px`;
  }
  return value;
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeNamedCollection = (items: any[] | null | undefined) => {
  if (!Array.isArray(items)) return [];

  return items
    .filter(Boolean)
    .map((item) => {
      if (typeof item === "object") {
        const derivedName =
          item.name ?? item.label ?? item.title ?? item.code ?? null;

        return {
          ...item,
          name: derivedName != null ? String(derivedName) : "",
        };
      }

      return {
        id: item,
        name: String(item),
      };
    })
    .filter((item) => item && String(item.name || "").trim().length > 0);
};

const extractItemIds = (items: any[] | null | undefined) => {
  if (!Array.isArray(items)) return [];

  return items
    .filter(Boolean)
    .map((item) => {
      if (typeof item === "object") return item.id;
      return item;
    })
    .filter((id) => id !== null && id !== undefined && id !== "");
};

const normalizeNotification = (notif: any) => ({
  ...notif,
  regulators: normalizeNamedCollection(notif?.regulators),
  classes: normalizeNamedCollection(notif?.classes),
});

const NotificationsTab: React.FC<IManageUserTabProps> = ({
  darkMode,
  selectedTeam,
  teamId,
  allTeamList,
  teamOptions,
  activeTab,
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();

  const [notifications, setNotifications] = useState<any[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAddNotifModal, setShowAddNotifModal] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [editNotification, setEditNotification] = useState<any | null>(null);
  const [notifToDelete, setNotifToDelete] = useState<number | null>(null);

  const listOuterRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<VariableSizeList | null>(null);
  const rowHeights = useRef<Map<RowKey, number>>(new Map());

  const listAreaRef = useRef<HTMLDivElement | null>(null);
  const [listHeight, setListHeight] = useState<number>(420);
  const [listAreaWidth, setListAreaWidth] = useState<number>(0);

  const canEdit = canUserEdit(user, selectedTeam);

  const dayMapping: Record<string, number> = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 0,
  };

  const reverseDayMapping: Record<number, string> = Object.fromEntries(
    Object.entries(dayMapping).map(([key, value]) => [value, key]),
  );

  const { data: notificationsData, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["notifications"],
    queryFn: fetchNotification,
    refetchOnWindowFocus: false,
  });

  const normalizedNotificationsData = useMemo(
    () => (Array.isArray(notificationsData) ? notificationsData.map(normalizeNotification) : []),
    [notificationsData],
  );

  const { mutate: mutateCreateNotification } = useMutation({
    mutationFn: async (notif: INewNotification) => createNotitfaction(notif),
    onSuccess: () => {
      refetch();
      toast.success(t("notification.addNotificationToaster"));
    },
  });

  const { mutate: mutateUpdateNotification } = useMutation({
    mutationFn: async (notif: UpdateNotificationPayload) => updateNotification(notif as any),
    onSuccess: () => {
      refetch();
      toast.success(t("notification.notificationChangeToaster"));
    },
  });

  const { mutate: mutateDeleteNotification } = useMutation({
    mutationFn: async (id: number) => deleteNotification(id),
    onSuccess: (_, deletedId) => {
      setNotifications((prev) => prev.filter((n) => n.id !== deletedId));
      toast.success(t("notification.deleteNotificationToaster"));
    },
  });

  useEffect(() => {
    if (selectedTeam && selectedTeam.id) {
      setNotifications(
        normalizedNotificationsData.filter((n: any) => n.team === selectedTeam.id),
      );
    } else {
      setNotifications(normalizedNotificationsData);
    }
  }, [selectedTeam, normalizedNotificationsData]);

  useEffect(() => {
    const update = () => {
      requestAnimationFrame(() => {
        const el = listAreaRef.current;
        if (!el) return;

        const h = el.clientHeight;
        const w = el.clientWidth;

        if (h > 0) setListHeight(h);
        if (w > 0) setListAreaWidth(w);
      });
    };

    update();

    const RO = (window as any).ResizeObserver;
    let ro: any = null;

    if (RO && listAreaRef.current) {
      ro = new RO(() => update());
      ro.observe(listAreaRef.current);
    }

    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const getSearchValue = (inputValue: string) => setSearchValue(inputValue.toLowerCase());

  const highlightText = useCallback(
    (text: string) => {
      if (!searchValue) return text;
      const safeSearch = escapeRegExp(searchValue);
      const regex = new RegExp(`(${safeSearch})`, "gi");
      return text.replace(
        regex,
        (match) =>
          `<span class='bg-yellow-300 text-slate-600 dark:text-slate-300 font-semibold'>${match}</span>`,
      );
    },
    [searchValue],
  );

  const filteredNotifications = useMemo(() => {
    if (!searchValue) return notifications;

    return notifications.filter((n: any) => {
      const teamName =
        allTeamList.find((t: ITeamList) => t.id === n.team)?.name?.toLowerCase() || "";
      const regulatorNames =
        (n.regulators || [])
          .map((r: any) => String(r?.name || "").toLowerCase())
          .join(" ") || "";
      const classNames =
        (n.classes || [])
          .map((c: any) => String(c?.name || "").toLowerCase())
          .join(" ") || "";
      const status = n.is_active ? "active" : "inactive";

      return (
        String(n.subject || "").toLowerCase().includes(searchValue) ||
        teamName.includes(searchValue) ||
        regulatorNames.includes(searchValue) ||
        classNames.includes(searchValue) ||
        status.includes(searchValue)
      );
    });
  }, [notifications, allTeamList, searchValue]);

  const responsiveMode = useMemo<ResponsiveMode>(() => {
    if (listAreaWidth > 0 && listAreaWidth < COMPACT_BREAKPOINT) return "compact";
    return "desktop";
  }, [listAreaWidth]);

  const hasTeamColumn = responsiveMode === "desktop";
  const hasRegulatorsColumn = responsiveMode === "desktop";
  const hasClassesColumn = responsiveMode === "desktop";
  const hasScheduleColumn = responsiveMode === "desktop";
  const hasActionsColumn = responsiveMode === "desktop";

  const showInlineMeta = responsiveMode === "compact";
  const showActionsInline = responsiveMode === "compact";

  const tableMinWidth = useMemo(() => {
    if (responsiveMode === "compact") return 240 + 360;

    return 160 + 140 + 180 + 160 + 200 + 160;
  }, [responsiveMode]);

  const GRID_STYLE = useMemo<React.CSSProperties>(() => {
    if (responsiveMode === "compact") {
      return {
        gridTemplateColumns: "minmax(240px,300px) minmax(360px,1fr)",
      };
    }

    return {
      gridTemplateColumns:
        "minmax(160px,1fr) 140px 180px 160px minmax(200px,1.3fr) 160px",
    };
  }, [responsiveMode]);

  const TABLE_GRID_STYLE = useMemo<React.CSSProperties>(
    () => ({
      ...GRID_STYLE,
      width: "100%",
      minWidth: `${tableMinWidth}px`,
    }),
    [GRID_STYLE, tableMinWidth],
  );

  const showLoaderRow = filteredNotifications.length > 0 && isFetching && !isLoading;
  const dataCount = filteredNotifications.length;
  const itemCount = dataCount + (showLoaderRow ? 1 : 0);

  const getItemKey = useCallback(
    (index: number): RowKey => {
      if (showLoaderRow && index === dataCount) return "__loader__";
      return filteredNotifications[index]?.id ?? index;
    },
    [filteredNotifications, showLoaderRow, dataCount],
  );

  const getRowSize = useCallback(
    (index: number) => {
      if (showLoaderRow && index === dataCount) return LOADER_ROW_H;
      return rowHeights.current.get(getItemKey(index)) ?? FALLBACK_ROW_H;
    },
    [getItemKey, showLoaderRow, dataCount],
  );

  const setRowSizeForIndex = useCallback(
    (index: number, size: number) => {
      if (!Number.isFinite(size) || size < 32) return;
      const key = getItemKey(index);
      const prev = rowHeights.current.get(key);
      if (prev !== size) {
        rowHeights.current.set(key, size);
        listRef.current?.resetAfterIndex(index, true);
      }
    },
    [getItemKey],
  );

  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [dataCount, searchValue, selectedTeam?.id, teamId, showLoaderRow, responsiveMode]);

  useEffect(() => {
    rowHeights.current = new Map();

    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = listAreaRef.current;
        if (el) {
          if (el.clientHeight > 0) setListHeight(el.clientHeight);
          if (el.clientWidth > 0) setListAreaWidth(el.clientWidth);
        }
        listRef.current?.resetAfterIndex(0, true);
        listRef.current?.scrollTo(0);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [activeTab, selectedTeam?.id, teamId]);

  useEffect(() => {
    listRef.current?.scrollTo(0);
  }, [searchValue, selectedTeam?.id, teamId, responsiveMode]);

  const shouldVirtualize = useMemo(
    () => filteredNotifications.length > VIRTUALIZE_ROW_THRESHOLD,
    [filteredNotifications.length],
  );

  const handleRemoveNotification = () => {
    if (notifToDelete !== null) {
      mutateDeleteNotification(notifToDelete);
      setNotifToDelete(null);
      setShowConfirmModal(false);
    }
  };

  const handleToggleStatus = (notif: INotification & { regulators?: any[]; classes?: any[] }) => {
    mutateUpdateNotification({
      id: notif.id,
      is_active: !((notif as any).is_active ?? false),
      regulators: extractItemIds(notif.regulators),
      classes: extractItemIds(notif.classes),
    });
  };

  const handleSubmitModal = (notification: any) => {
    if (editNotification && notification.id) mutateUpdateNotification(notification as any);
    else mutateCreateNotification(notification as any);

    setEditNotification(null);
    setShowAddNotifModal(false);
  };

  const HeaderRow = useCallback(
    () => (
      <div
        className={[
          "grid items-center h-11 sticky top-0 z-20",
          "bg-white dark:bg-slate-900",
          "text-xs font-semibold uppercase tracking-wide",
          "text-slate-600 dark:text-slate-300",
          "border-b border-slate-200 dark:border-slate-700",
          "divide-x divide-slate-200 dark:divide-slate-700",
        ].join(" ")}
        style={{
          ...TABLE_GRID_STYLE,
          boxSizing: "border-box",
        }}
      >
        <div className="px-4 py-3 min-w-0 truncate whitespace-nowrap">
          {t("notification.subject")}
        </div>
        {hasTeamColumn && (
          <div className="px-4 py-3 min-w-0 truncate whitespace-nowrap">
            {t("notification.team")}
          </div>
        )}
        {hasRegulatorsColumn && (
          <div className="px-4 py-3 min-w-0 truncate whitespace-nowrap">
            {t("notification.regulators")}
          </div>
        )}
        {hasClassesColumn && (
          <div className="px-4 py-3 min-w-0 truncate whitespace-nowrap">
            {t("notification.classes")}
          </div>
        )}
        {hasScheduleColumn && (
          <div className="px-4 py-3 min-w-0 truncate whitespace-nowrap">
            {t("notification.schedule")}
          </div>
        )}
        {hasActionsColumn && (
          <div className="px-4 py-3 text-center min-w-0 truncate whitespace-nowrap">
            {t("adminPanel.columns.actions", { defaultValue: "Actions" })}
          </div>
        )}
      </div>
    ),
    [
      t,
      TABLE_GRID_STYLE,
      hasTeamColumn,
      hasRegulatorsColumn,
      hasClassesColumn,
      hasScheduleColumn,
      hasActionsColumn,
    ],
  );

  const OuterElement = useMemo(() => {
    const C = React.forwardRef<HTMLDivElement, any>(({ style, className, ...rest }, ref) => (
      <div
        ref={ref}
        {...rest}
        className={["relative", className].filter(Boolean).join(" ")}
        style={{
          ...style,
          overflow: "auto",
          ...({ overscrollBehavior: "contain" } as any),
          ...({ scrollbarGutter: "stable both-edges" } as any),
        }}
      />
    ));
    C.displayName = "NotificationsOuterElement";
    return C;
  }, []);

  const InnerElement = useMemo(() => {
    const C = React.forwardRef<HTMLDivElement, any>(({ style, children, ...rest }, ref) => {
      const rowsHeight = style?.height;

      return (
        <div
          ref={ref}
          {...rest}
          style={{
            ...style,
            height: addPx(rowsHeight, HEADER_H),
            minWidth: `${tableMinWidth}px`,
          }}
        >
          <HeaderRow />
          <div
            style={{
              position: "relative",
              height: rowsHeight,
              minWidth: `${tableMinWidth}px`,
            }}
          >
            {children}
          </div>
        </div>
      );
    });

    C.displayName = "NotificationsInnerElement";
    return C;
  }, [HeaderRow, tableMinWidth]);

  const Row = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const isLoader = showLoaderRow && index === dataCount;
    const notif: any = isLoader ? null : filteredNotifications[index];

    const rowRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
      if (!notif || isLoader) return;

      const measure = () => {
        if (!rowRef.current) return;
        setRowSizeForIndex(index, rowRef.current.getBoundingClientRect().height);
      };

      requestAnimationFrame(measure);

      const RO = (window as any).ResizeObserver;
      if (!RO || !rowRef.current) return;

      const ro = new RO(() => measure());
      ro.observe(rowRef.current);
      return () => ro.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, notif, isLoader, searchValue, responsiveMode]);

    if (isLoader) {
      return (
        <div style={style} className="flex items-center justify-center">
          <FaSpinner
            className="animate-spin inline-block"
            size={18}
            color={darkMode ? "#FFFFFF" : "#000000"}
          />
        </div>
      );
    }

    if (!notif) return <div style={style} />;

    const safeRegulators = Array.isArray(notif.regulators) ? notif.regulators.filter(Boolean) : [];
    const safeClasses = Array.isArray(notif.classes) ? notif.classes.filter(Boolean) : [];

    const teamName =
      allTeamList.find((t: ITeamList) => t.id === notif.team)?.name || "-";

    const teamObj = allTeamList.find((t) => t.id === notif.team) as any;
    const rowCanEdit = selectedTeam ? canEdit : canUserEdit(user, teamObj);

    const editCls = `tooltip-edit-notif-${notif.id}`;
    const delCls = `tooltip-del-notif-${notif.id}`;

    const scheduleNode = notif.schedule ? (
      <div className="flex flex-col gap-0.5 text-sm min-w-0">
        <span className="min-w-0">
          <span className="text-slate-500 dark:text-slate-400">
            {t("notification.days")}:{" "}
          </span>
          <span className="text-slate-600 dark:text-slate-300">
            {notif.schedule.day_of_week
              ? String(notif.schedule.day_of_week)
                  .split(",")
                  .map((num: string) => reverseDayMapping[Number(num)] || num)
                  .join(", ")
              : "-"}
          </span>
        </span>
        <span className="min-w-0">
          <span className="text-slate-500 dark:text-slate-400">
            {t("notification.time")}:{" "}
          </span>
          <span className="text-slate-600 dark:text-slate-300">
            {String(notif.schedule.hour ?? "").padStart(2, "0")}:
            {String(notif.schedule.minute ?? "").padStart(2, "0")}
          </span>
        </span>
        <span className="truncate min-w-0">
          <span className="text-slate-500 dark:text-slate-400">
            {t("notification.timezone")}:{" "}
          </span>
          <span className="text-slate-600 dark:text-slate-300">
            {notif.schedule.timezone || "-"}
          </span>
        </span>
      </div>
    ) : (
      <span className="text-slate-600 dark:text-slate-300">-</span>
    );

    const actions = rowCanEdit ? (
      <>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-600 dark:text-slate-300">
            {notif.is_active ? t("notification.on") : t("notification.off")}
          </span>
          <button
            type="button"
            onClick={() => handleToggleStatus(notif)}
            className={[
              "relative inline-flex h-5 w-10 items-center rounded-full transition-colors",
              notif.is_active ? "bg-del-green" : "bg-slate-300 dark:bg-slate-600",
            ].join(" ")}
            aria-label="Toggle notification status"
          >
            <span
              className={[
                "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                notif.is_active ? "translate-x-5" : "translate-x-1",
              ].join(" ")}
            />
          </button>
        </div>

        <button
          className={`inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition ${editCls}`}
          onClick={() => {
            setEditNotification(notif);
            setShowAddNotifModal(true);
          }}
          aria-label="Edit notification"
        >
          <AiOutlineEdit
            size={16}
            className="text-slate-600 dark:text-slate-200"
          />
        </button>
        <Tooltip
          anchorSelect={`.${editCls}`}
          place="top"
          className="text-xs font-light z-50"
          content={t("notification.editNotification")}
        />

        <DeleteButton
          onClick={() => {
            setNotifToDelete(notif.id);
            setShowConfirmModal(true);
          }}
          className={delCls}
          ariaLabel="Delete notification"
        />
        <Tooltip
          anchorSelect={`.${delCls}`}
          place="top"
          className="text-xs font-light z-50"
          content={t("notification.deleteNotification")}
        />
      </>
    ) : (
      <span className="text-slate-400 text-sm">—</span>
    );

    return (
      <div style={style}>
        <div
          ref={rowRef}
          className={[
            "grid items-stretch text-sm",
            "border-b border-slate-200 dark:border-slate-700",
            "divide-x divide-slate-200 dark:divide-slate-700",
            index % 2 === 0 ? "bg-white/50 dark:bg-slate-900/15" : "bg-transparent",
            "hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors",
          ].join(" ")}
          style={{
            ...TABLE_GRID_STYLE,
            boxSizing: "border-box",
          }}
        >
          <div className="px-4 py-3 min-w-0 h-full">
            <div className="min-w-0">
              <div
                className="min-w-0 overflow-hidden truncate font-medium text-slate-600 dark:text-slate-300"
                dangerouslySetInnerHTML={{
                  __html: highlightText(notif.subject || "-"),
                }}
              />

              {showInlineMeta && (
                <div
                  className="mt-1 min-w-0 overflow-hidden truncate text-xs text-slate-500 dark:text-slate-400"
                  dangerouslySetInnerHTML={{
                    __html: highlightText(teamName || "-"),
                  }}
                />
              )}
            </div>
          </div>

          {responsiveMode === "compact" ? (
            <div className="px-4 py-3 min-w-0 h-full">
              <div className="flex flex-col gap-2 min-w-0">
                <div className="min-w-0">
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                    {t("notification.regulators")}
                  </div>
                  <OverflowCell
                    notifId={`regulators-${notif.id}`}
                    values={safeRegulators}
                    highlightText={highlightText}
                    label="regulators"
                  />
                </div>

                <div className="min-w-0">
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                    {t("notification.classes")}
                  </div>
                  <OverflowCell
                    notifId={`classes-${notif.id}`}
                    values={safeClasses}
                    highlightText={highlightText}
                    label="classes"
                  />
                </div>

                <div className="min-w-0">
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                    {t("notification.schedule")}
                  </div>
                  {scheduleNode}
                </div>

                {showActionsInline && (
                  <div className="pt-1 flex items-center justify-end gap-3">{actions}</div>
                )}
              </div>
            </div>
          ) : (
            <>
              {hasTeamColumn && (
                <div className="px-4 py-3 h-full min-w-0 text-slate-600 dark:text-slate-300">
                  <div
                    className="min-w-0 overflow-hidden truncate"
                    dangerouslySetInnerHTML={{ __html: highlightText(teamName || "-") }}
                  />
                </div>
              )}

              {hasRegulatorsColumn && (
                <div className="px-4 py-3 h-full min-w-0 text-slate-600 dark:text-slate-300">
                  <OverflowCell
                    notifId={`regulators-${notif.id}`}
                    values={safeRegulators}
                    highlightText={highlightText}
                    label="regulators"
                  />
                </div>
              )}

              {hasClassesColumn && (
                <div className="px-4 py-3 h-full min-w-0 text-slate-600 dark:text-slate-300">
                  <OverflowCell
                    notifId={`classes-${notif.id}`}
                    values={safeClasses}
                    highlightText={highlightText}
                    label="classes"
                  />
                </div>
              )}

              {hasScheduleColumn && (
                <div className="px-4 py-3 h-full min-w-0 text-slate-600 dark:text-slate-300">
                  {scheduleNode}
                </div>
              )}

              {hasActionsColumn && (
                <div className="px-4 py-3 h-full flex items-center justify-center gap-3 min-w-0">
                  {actions}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const showingText = useMemo(() => {
    const shown = filteredNotifications.length;
    const total = notifications.length;
    const fallback = `Showing ${shown} of ${total} notifications`;

    const translated = t("notification.showingNotifications", {
      shown,
      total,
      count: shown,
      filtered: shown,
      filteredCount: shown,
      totalCount: total,
      notifications: total,
      totalNotifications: total,
      defaultValue: fallback,
    });

    if (
      typeof translated === "string" &&
      (translated.includes("...") ||
        translated.includes("{{") ||
        translated.includes("undefined"))
    ) {
      return fallback;
    }
    return translated;
  }, [t, filteredNotifications.length, notifications.length]);

  const btnBase =
    "inline-flex items-center justify-center h-10 px-4 rounded-lg text-sm font-medium transition";
  const btnGreen = `${btnBase} bg-del-green text-white hover:opacity-90`;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {t("notifications", { defaultValue: "Notifications" })}{" "}
              {selectedTeam ? `(${selectedTeam.name})` : ""}
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t("notification.subtitle", {
                defaultValue: "Create, edit, and manage scheduled notifications",
              })}
            </p>
          </div>

          {canEdit && (
            <button className={btnGreen} onClick={() => setShowAddNotifModal(true)}>
              <FaPlus className="mr-2" />
              {t("notification.createNotification")}
            </button>
          )}
        </div>

        <div className="mt-4 max-w-md">
          <SearchTableInput
            onInputChange={getSearchValue}
            placeholderText={t("notification.searchNotification")}
          />
        </div>

        {showAddNotifModal && (
          <AddEditNotificationModal
            setShowAddNotificationModal={(val: boolean) => {
              setShowAddNotifModal(val);
              if (!val) setEditNotification(null);
            }}
            selectedTeam={selectedTeam}
            teamListOptions={teamOptions}
            darkMode={darkMode}
            editNotification={editNotification}
            onSubmit={handleSubmitModal}
          />
        )}
      </div>

      <div className="flex-1 min-h-0 bg-slate-100 dark:bg-slate-950/20 p-6 overflow-hidden">
        <div className="h-full min-h-0 flex flex-col">
          <div className="flex-1 min-h-0">
            <div className="h-full bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col">
              {isLoading ? (
                <div className="text-center p-6">
                  <FaSpinner
                    className="animate-spin inline-block"
                    size={24}
                    color={darkMode ? "#FFFFFF" : "#000000"}
                  />
                </div>
              ) : (
                <>
                  <div ref={listAreaRef} className="flex-1 min-h-0 relative flex flex-col">
                    {filteredNotifications.length === 0 ? (
                      <div className="h-full min-h-0 overflow-auto">
                        <div style={{ minWidth: tableMinWidth }}>
                          <HeaderRow />
                          <div className="p-6 flex items-center justify-center min-h-[220px]">
                            <p className="text-sm text-slate-600 dark:text-slate-300">
                              {t("noData")}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : shouldVirtualize ? (
                      <VariableSizeList
                        ref={listRef as any}
                        outerRef={listOuterRef as any}
                        outerElementType={OuterElement as any}
                        innerElementType={InnerElement as any}
                        height={listHeight}
                        itemCount={itemCount}
                        itemSize={getRowSize as any}
                        width="100%"
                        itemKey={(index) => getItemKey(index)}
                        overscanCount={5}
                      >
                        {Row as any}
                      </VariableSizeList>
                    ) : (
                      <div className="h-full min-h-0 overflow-auto">
                        <div style={{ minWidth: tableMinWidth }}>
                          <HeaderRow />
                          {filteredNotifications.map((notif, index) => (
                            <Row
                              key={notif.id ?? index}
                              index={index}
                              style={{}}
                            />
                          ))}

                          {showLoaderRow && (
                            <div
                              className="flex items-center justify-center"
                              style={{ height: LOADER_ROW_H }}
                            >
                              <FaSpinner
                                className="animate-spin inline-block"
                                size={18}
                                color={darkMode ? "#FFFFFF" : "#000000"}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                    {showingText}
                  </div>
                </>
              )}
            </div>
          </div>

          {showConfirmModal && (
            <ModalConfirmWindow
              setShowModal={() => setShowConfirmModal(false)}
              handleDeleteUser={handleRemoveNotification}
              message={t("notification.deleteNotificationMsg")}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export { NotificationsTab };